import { useProjectId } from '../../../hooks/useProjectId';
import { useLanguage } from '../../../hooks/useLanguage';
import { DateTime } from 'luxon';
import { useGetNamesFromUserIdsQuery } from '../../../entry-form-v2/data/useGetNamesFromUserIdsQuery';
import { Skeleton } from '../../../entry-form-v2/component/Skeleton';
import { LanguageType } from '../../../../types';
import { Eye } from 'lucide-react';
import {
	AccessHistoryEventType,
	useGetCodelistRecordAccessHistoryQuery
} from './useGetCodelistRecordAccessHistoryQuery';

export const AccessHistoryTab = ({ originalEntryId }: { originalEntryId: string }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];
	const projectId = useProjectId();

	const accessHistoryQuery = useGetCodelistRecordAccessHistoryQuery({
		projectId,
		originalEntryId
	});

	const hasUserNames = (accessHistoryQuery.data?.events.length ?? 0) > 0;

	const userNamesByUserIdQuery = useGetNamesFromUserIdsQuery(
		{
			userIds: accessHistoryQuery.data?.events.map(event => event.userId) ?? []
		},
		{
			enabled: !!hasUserNames
		}
	);

	const isLoading = accessHistoryQuery.isLoading || userNamesByUserIdQuery.isLoading;

	if (!accessHistoryQuery.data && isLoading) {
		return <LoadingView />;
	}

	if (!accessHistoryQuery.data) {
		return null;
	}

	return (
		<ul className="flex flex-col gap-4 text-base">
			{accessHistoryQuery.data.events.map(event => (
				<li key={event.id} className="p-4 border border-gray-200 rounded-lg">
					<div className="flex flex-row justify-between">
						<div className="flex flex-row gap-2 items-center font-bold">
							<AccessEventTypeIcon eventType={event.eventType} />

							{userNamesByUserIdQuery.data?.namesFromUserIds[event.userId]
								? `${userNamesByUserIdQuery.data.namesFromUserIds[event.userId]} ${
										translation.eventTypeTitle[event.eventType]
								  }`
								: translation.unknownUser}
						</div>

						<div>
							{DateTime.fromISO(event.createdAt).toLocaleString(
								DateTime.DATETIME_MED
							)}
						</div>
					</div>
				</li>
			))}
		</ul>
	);
};

const AccessEventTypeIcon = (props: { eventType: AccessHistoryEventType }) => {
	switch (props.eventType) {
		case 'GET_RECORD':
			return <Eye className="size-7" />;
	}
};

const LoadingView = () => (
	<ul className="flex flex-col gap-4">
		{new Array(6).fill(null).map((_, index) => (
			<li key={index} className="p-4 border border-gray-200 rounded-lg">
				<Skeleton className="w-40 h-6 rounded-md" />
				<Skeleton className="w-48 h-4 mt-2 rounded-md" />
			</li>
		))}
	</ul>
);

const DICTIONARY = {
	[LanguageType.English]: {
		unknownUser: 'Unknown user',
		eventTypeTitle: {
			GET_RECORD: 'viewed the record'
		}
	},
	[LanguageType.Norwegian]: {
		unknownUser: 'Ukjent bruker',
		eventTypeTitle: {
			GET_RECORD: 'så på raden'
		}
	}
};
